import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { FuseAlertModule } from '../../../../@fuse/components/alert/alert.module';
import { FuseCardModule } from '../../../../@fuse/components/card/card.module';
import { FuseDrawerModule } from '../../../../@fuse/components/drawer/drawer.module';
import { PipesModule } from '../../../core/pipes/pipes.module';
import { FuseDrawerComponent } from '../../../../@fuse/components/drawer/drawer.component';
import { SystemChangesForUsers } from '../../../core/system-changes/system-changes.model';
import { UserNodePrivileges } from '../../../modules/admin/projects/projects.types';
import { AuthService } from '../../../core/auth/auth.service';
import { SystemChangesService } from '../../../core/system-changes/system-changes.service';
import { UserService } from '../../../core/user/user.service';
import { LoginResponse } from '../../../core/enums/login.response.enum';

@Component({
    selector: 'system-changes',
    templateUrl: './system-changes.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
    MatIconModule,
    FuseAlertModule,
    FuseCardModule,
    FuseDrawerModule,
    MatButtonModule,
    PipesModule,
    TranslocoModule
],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class SystemChangesComponent implements OnInit, OnDestroy {

    @ViewChild('alertChangesPanel') private _alertChangesPanel: TemplateRef<any>;
    @ViewChild('changesOrigin') private _changesOrigin: MatButton;
    @ViewChild('changesDrawer') changesDrawer: FuseDrawerComponent;

    systemChanges: SystemChangesForUsers[] = [];
    role: UserNodePrivileges;
    notify = false;
    shakeState = false;

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private authService: AuthService,
        private _overlay: Overlay,
        private systemChangesService: SystemChangesService,
        private userService: UserService,
        private _viewContainerRef: ViewContainerRef,
    ) { }

    ngOnInit(): void {
        this.systemChangesService.systemChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(systemChanges => {
                this.systemChanges = systemChanges;
            });

        this.systemChangesService.notify$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(notify => {
                this.notify = notify;
                if (notify) {
                    this.shakeState = true;
                    setTimeout(() => this.shakeState = false, 2000);
                    this.openPanel();
                }
            });

        
        this.authService.isLoggedIn().then(async loggedIn => {
            if (loggedIn === LoginResponse.LOGIN_SUCCESS) {
                this.systemChangesService.retrieveSystemChangesForUsers();
                this.role = await firstValueFrom(this.userService.getRole());
            }
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    toggleDrawer() {
        this.notify = false;
        this.changesDrawer.toggle();
    }

    openPanel(): void {
        // Return if the messages panel or its origin is not defined
        if (!this._alertChangesPanel || !this._changesOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        if (!this._overlayRef.hasAttached()) {
            this._overlayRef.attach(new TemplatePortal(this._alertChangesPanel, this._viewContainerRef));
        }
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._changesOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
